import { supabase } from "../supabaseClient";
import { formatInstitutionEffectiveRate, formatInstitutionPrice } from "./institutionPricing";

export const loadInstitutionBillingRows = async () => {
  const { data, error } = await supabase
    .from("billing_prices")
    .select("price_key, amount_cents, currency, interval, metadata, billing_products!inner(id, product_key, name, product_line, metadata, active)")
    .eq("active", true)
    .eq("billing_products.active", true)
    .eq("billing_products.product_line", "institutions");

  if (error) throw error;
  return data || [];
};

const asNumber = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const next = Number(value);
  return Number.isFinite(next) ? next : null;
};

const productName = (product) => {
  const name = String(product?.name || "").replace(/\s+(monthly|yearly)$/i, "").trim();
  return name || null;
};

const planSizeFromMetadata = (plan, metadata) => {
  const limit = asNumber(metadata?.member_limit) ?? asNumber(metadata?.display_member_limit);
  const included = asNumber(metadata?.included_members);
  if (metadata?.usage_based && included) return `Up to ${included.toLocaleString()} members included`;
  if (!limit) return plan.size;
  const noun = plan.segment === "Club" ? "members" : "people";
  return `Up to ${limit.toLocaleString()} ${noun}`;
};

const firstPaidOverageBand = (metadata) => (
  Array.isArray(metadata?.overage_bands)
    ? metadata.overage_bands.find((band) => asNumber(band?.amount_cents) > 0)
    : null
);

const perMemberLabel = (plan, monthlyAmountCents, metadata) => {
  if (!monthlyAmountCents) return plan.perMember || null;
  if (metadata?.usage_based) {
    const included = asNumber(metadata.included_members);
    const paidBands = Array.isArray(metadata?.overage_bands)
      ? metadata.overage_bands.filter((band) => asNumber(band?.amount_cents) > 0)
      : [];
    const band = firstPaidOverageBand(metadata);
    const rate = asNumber(band?.amount_cents);
    if (included && paidBands.length > 1) {
      return `${included.toLocaleString()} members included, tiered overage pricing`;
    }
    if (included && rate) {
      return `${included.toLocaleString()} members included, then ${formatInstitutionPrice(rate)}/member/month`;
    }
    return plan.perMember || null;
  }

  const limit = asNumber(metadata?.member_limit) ?? asNumber(metadata?.display_member_limit) ?? asNumber(plan.memberLimit);
  if (!limit) return plan.perMember || null;
  const noun = plan.segment === "Club" ? "members" : "people";
  return `${formatInstitutionEffectiveRate(monthlyAmountCents, limit)}/member/month at ${limit.toLocaleString()} ${noun}`;
};

const fallbackAmount = (plan, cycle) => {
  const base = asNumber(plan.baseAmountCents);
  if (cycle === "yearly") {
    const yearly = asNumber(plan.yearlyAmountCents);
    if (yearly !== null) return yearly;
    return base !== null ? Math.round(base * 10.2) : null;
  }
  return base;
};

export const mergeInstitutionPlansWithBillingRows = (plans, billingRows = []) => {
  const products = new Map();
  const amounts = new Map();
  const priceMetadata = new Map();

  billingRows.forEach((row) => {
    const product = row?.billing_products;
    if (!product?.product_key) return;
    products.set(product.product_key, product);
    if (typeof row.amount_cents === "number") {
      amounts.set(product.product_key, row.amount_cents);
    }
    if (row.metadata && typeof row.metadata === "object") {
      priceMetadata.set(product.product_key, row.metadata);
    }
  });

  return plans.map((plan) => {
    const monthlyProduct = products.get(plan.monthlyProductKey);
    const yearlyProduct = products.get(plan.yearlyProductKey);
    const metadata = {
      ...(plan.metadata || {}),
      ...((monthlyProduct?.metadata || yearlyProduct?.metadata) || {}),
      ...((priceMetadata.get(plan.monthlyProductKey) || priceMetadata.get(plan.yearlyProductKey)) || {}),
    };
    const monthlyAmountCents = amounts.get(plan.monthlyProductKey) ?? fallbackAmount(plan, "monthly");
    const yearlyAmountCents = amounts.get(plan.yearlyProductKey) ?? fallbackAmount(plan, "yearly");
    const memberLimit = asNumber(metadata.member_limit) ?? asNumber(metadata.display_member_limit) ?? plan.memberLimit;
    const displayName = plan.name || productName(monthlyProduct) || productName(yearlyProduct);

    return {
      ...plan,
      name: displayName,
      title: plan.title || displayName,
      metadata,
      memberLimit,
      baseAmountCents: monthlyAmountCents ?? plan.baseAmountCents,
      monthlyAmountCents,
      yearlyAmountCents,
      monthly: monthlyAmountCents !== null ? formatInstitutionPrice(monthlyAmountCents) : plan.monthly,
      yearly: yearlyAmountCents !== null ? formatInstitutionPrice(yearlyAmountCents) : plan.yearly,
      price: monthlyAmountCents !== null ? metadata.usage_based ? `From ${formatInstitutionPrice(monthlyAmountCents)}/month` : `${formatInstitutionPrice(monthlyAmountCents)}/month` : plan.price,
      monthlyPrice: plan.cardPriceLabel || (monthlyAmountCents !== null ? `${formatInstitutionPrice(monthlyAmountCents)}/month` : plan.monthlyPrice),
      yearlyPrice: plan.cardPriceLabel ? plan.cardPriceLabel : yearlyAmountCents !== null ? `${formatInstitutionPrice(yearlyAmountCents)}/year` : plan.yearlyPrice,
      size: planSizeFromMetadata(plan, metadata),
      perMember: perMemberLabel(plan, monthlyAmountCents, metadata),
    };
  });
};
